import { Dispatch, SetStateAction } from 'react';

import { Capability } from '@/hooks/useCapabilities';
import { ParameterInput } from '@/sections/attacks/capabilities/components/inputs';

import { OutputSettings } from './OutputSettings';

interface ParametersSectionProps {
  capability: Capability;
  parameterValues: Record<string, string>;
  setParameterValues: Dispatch<SetStateAction<Record<string, string>>>;
  validationErrors: Record<string, boolean>;
  setValidationErrors: Dispatch<SetStateAction<Record<string, boolean>>>;
  isPraetorianUser: boolean;
  largeArtifact: boolean;
  setLargeArtifact: (value: boolean) => void;
  onParameterBlur?: (paramName: string) => void;
}

/**
 * ParametersSection - Component for displaying and editing capability parameters
 *
 * Displays configurable parameters for a capability, with different views
 * for Praetorian users (editable) and customers (read-only).
 */
export function ParametersSection({
  capability,
  parameterValues,
  setParameterValues,
  validationErrors,
  setValidationErrors,
  isPraetorianUser,
  largeArtifact,
  setLargeArtifact,
  onParameterBlur,
}: ParametersSectionProps) {
  // Handler for parameter value changes
  const handleParameterChange = (paramName: string, value: string) => {
    setParameterValues(prev => ({
      ...prev,
      [paramName]: value,
    }));
    // Clear validation error when user starts typing
    if (validationErrors[paramName]) {
      setValidationErrors(prev => ({
        ...prev,
        [paramName]: false,
      }));
    }
  };

  if (!capability.parameters || capability.parameters.length === 0) {
    return (
      <div className="h-fit">
        {/* Output Settings - Only show for capabilities that support big output */}
        {isPraetorianUser && capability.largeArtifact && (
          <OutputSettings
            largeArtifact={largeArtifact}
            setLargeArtifact={setLargeArtifact}
          />
        )}
        <div className="py-8 text-center">
          <p className="text-chariot-text-secondary">
            This capability has no configurable parameters.
          </p>
        </div>
      </div>
    );
  }

  // Separate required and optional parameters
  const requiredParams = capability.parameters.filter(
    param => param.required === true
  );
  const optionalParams = capability.parameters.filter(
    param => param.required === false || param.required === undefined
  );

  return (
    <div className="h-fit">
      {/* Required Parameters */}
      {requiredParams.length > 0 && (
        <div className="mb-8">
          <div className="flex flex-col gap-3">
            {requiredParams.map((param, index) => (
              <ParameterInput
                key={`required-${index}`}
                param={param}
                value={parameterValues[param.name] || ''}
                onChange={value => handleParameterChange(param.name, value)}
                onBlur={onParameterBlur}
                error={
                  validationErrors[param.name]
                    ? `${param.name} is required`
                    : undefined
                }
                readOnly={!isPraetorianUser}
                isRequired={true}
              />
            ))}
          </div>
        </div>
      )}

      {/* Output Settings - Only show for capabilities that support big output */}
      {isPraetorianUser && capability.largeArtifact && (
        <OutputSettings
          largeArtifact={largeArtifact}
          setLargeArtifact={setLargeArtifact}
        />
      )}

      {/* Optional Parameters */}
      {optionalParams.length > 0 && (
        <div>
          <h4 className="text-md text-chariot-text-primary mb-4 font-medium">
            Optional Parameters
          </h4>
          <div className="flex flex-col gap-3">
            {optionalParams.map((param, index) => (
              <ParameterInput
                key={`optional-${index}`}
                param={param}
                value={parameterValues[param.name] || ''}
                onChange={value => handleParameterChange(param.name, value)}
                readOnly={!isPraetorianUser}
                isRequired={false}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
